/**
 * One of the person's conversation groups in the rail (owner 2026-10-31: "grouping conversations does not work" —
 * "Move to group" saved the group and the rail never showed it). A folder row that opens on its conversations, pinned
 * first; it reads them only while open. Same accordion as the rail's Dashboards section.
 */
import type React from 'react';
import { useState } from 'react';
import { ChevronRight, Folder } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import type { ConversationGroup } from '../../generated/api/types.gen.js';
import { useSimpleConversations } from '../../hooks/useSimpleConversations.js';
import { ConversationRailRow } from './ConversationRailRow.js';
import { railRowClass } from './rail-row.js';

interface Props {
  group: ConversationGroup;
  activeModuleId: string;
  selectedConversationId: string | null;
  liveIds: ReadonlySet<string>;
  showTags: boolean;
  onSelectConversation: (conversationId: string) => void;
}

export const ConversationGroupSection: React.FC<Props> = ({
  group,
  activeModuleId,
  selectedConversationId,
  liveIds,
  showTags,
  onSelectConversation,
}) => {
  const [open, setOpen] = useState(false);
  const conversations = useSimpleConversations(activeModuleId, { groupId: group.id, enabled: open });
  const rows = [...(conversations.data ?? [])].sort((a, b) => Number(b.pinnedAt != null) - Number(a.pinnedAt != null));

  return (
    <div className="flex flex-col gap-0.5">
      <Button
        variant="ghost"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={railRowClass({ className: 'text-body' })}
      >
        <ChevronRight size={12} className={`shrink-0 text-faint transition-transform ${open ? 'rotate-90' : ''}`} />
        <Folder size={12} className="shrink-0 text-muted" aria-hidden />
        <span className="overflow-hidden text-ellipsis">{group.name}</span>
        <span className="ml-auto font-mono text-[10.5px] text-faint">{group.conversationCount}</span>
      </Button>

      {open && (
        <div className="flex flex-col gap-0.5 pl-3">
          {conversations.isPending ? (
            <p className="px-2.5 py-1 text-xs text-faint">Loading…</p>
          ) : conversations.isError ? (
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-xs"
              onClick={() => void conversations.refetch()}
            >
              Could not load this group. Retry
            </Button>
          ) : rows.length === 0 ? (
            // The count is the whole organization's; the rail may be scoped to one module.
            <p className="px-2.5 py-1 text-xs text-faint">No conversation of this group in this view.</p>
          ) : (
            rows.map((conversation) => (
              <ConversationRailRow
                key={String(conversation.id)}
                conversation={conversation}
                active={String(conversation.id) === selectedConversationId}
                live={liveIds.has(String(conversation.id))}
                showTag={showTags}
                onSelect={onSelectConversation}
              />
            ))
          )}
          {conversations.hasOlder && (
            <Button
              variant="ghost"
              size="sm"
              className="justify-start text-xs text-muted"
              loading={conversations.loadingOlder}
              onClick={conversations.showOlder}
            >
              Show more
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
